import { isDefined, zipper } from "../utils";
import sma from "./sma";
import ema from "./ema";
import { TrendIntensity as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { period, sourcePath, signalPeriod } = options;

    // Step 1: Calculate SMA of the source field
    const smaCalculator = sma().options({ windowSize: period, sourcePath });

    const smaData = smaCalculator(data);

    // Step 2: Calculate gain/loss using rolling window of period/2
    const maxLength = Math.ceil(period / 2);
    const tiiValues = [];

    let gain = 0;
    let loss = 0;
    const queue = [];

    for (let i = 0; i < data.length; i++) {
      const bar = data[i];
      const smaValue = smaData[i];

      if (
        !isDefined(bar) ||
        !isDefined(bar[sourcePath]) ||
        !isDefined(smaValue)
      ) {
        tiiValues.push(undefined);
        continue;
      }

      // Calculate change from SMA
      const change = bar[sourcePath] - smaValue;

      // Update gain/loss
      if (change < 0) {
        loss += Math.abs(change);
      } else {
        gain += change;
      }

      // Add to queue
      queue.push(change);

      // Maintain rolling window size
      if (queue.length > maxLength) {
        const oldChange = queue.shift();
        if (oldChange < 0) {
          loss -= Math.abs(oldChange);
        } else {
          gain -= oldChange;
        }
      }

      // Calculate TII value
      // Formula: TII = 100 - (100 / (1 + (gain/loss)))
      let tiiValue;
      if (Math.abs(loss) < 0.00000001) {
        tiiValue = 100;
      } else {
        tiiValue = 100 - 100 / (1 + gain / loss);
      }

      tiiValues.push(tiiValue);
    }

    // Step 3: Apply EMA to TII to create signal line
    const tiiDataForEMA = tiiValues.map((tii) =>
      isDefined(tii) ? { value: tii } : undefined,
    );

    const emaCalculator = ema().options({
      windowSize: signalPeriod,
      sourcePath: "value",
    });

    const signalData = emaCalculator(tiiDataForEMA);

    // Step 4: Combine TII and Signal
    const zip = zipper().combine((tii, signal) => ({
      tii: tii,
      signal: signal,
    }));

    const result = zip(tiiValues, signalData);

    return result;
  }

  calculator.undefinedLength = function () {
    const { period, signalPeriod } = options;
    // SMA period + signal EMA period - 1
    return period + signalPeriod - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
